import React, { useState, useEffect } from "react";
import "./Recharge.css";
import { apiList } from "../../helpers/apiList";
import Swal from "sweetalert2";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import MobileVectorIcon from "../../assets/Icons/Mobile Vector.png";
import MobileVectorWhiteIcon from "../../assets/Icons/Mobile Vector White.png";
import DTHVectorIcon from "../../assets/Icons/DTH Vector.png";
import DTHVectorWhiteIcon from "../../assets/Icons/DTH Vector White.png";
import { useSelector } from "react-redux";

const tabs = [
  { id: "mobile", label: "Mobile" },
  { id: "dth", label: "DTH" },
];

const Recharge = ({ handleClose, callApi }) => {
  const [activeTab, setActiveTab] = useState("mobile");
  const loggedInUserName = sessionStorage.getItem("username");
  const { rechargeKey } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);

  // Mobile form states
  const [mobileForm, setMobileForm] = useState({
    mobileNumber: "",
    operator: "",
    circle: "",
    amount: "",
    serviceName: "",
  });
  const [mobileErrors, setMobileErrors] = useState({});
  const [mobileInputType, setMobileInputType] = useState("text");
  const [operatorList, setOperatorList] = useState([]);

  // DTH form states
  const [dthForm, setDthForm] = useState({
    subscriberId: "",
    operator: "",
    amount: "",
    tpin: "",
  });
  const [dthErrors, setDthErrors] = useState({});
  const [dthInputType, setDthInputType] = useState("text");

  useEffect(() => {
    const fetchOperatorsCode = () => {
      const payload = {
        username: loggedInUserName,
        serviceName: activeTab === "mobile" ? "MOBILE" : "DTH",
      };

      axiosPostApi(apiList.FETCH_OPERATOR_CODES, payload)
        .then((response) => {
          if (response?.status === 200 && response?.data?.respCode === "00") {
            setOperatorList(response?.data?.data);
          } else {
            CustomAlert({
              title: "Failed to Request",
              text: response.data.message,
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching dashboard data:", error);
        });
    };

    fetchOperatorsCode();
  }, [activeTab]);

  useEffect(() => {
    setMobileForm({
      mobileNumber: "",
      operator: "",
      circle: "",
      amount: "",
      tpin: "",
    });
    setDthForm({
      subscriberId: "",
      operator: "",
      amount: "",
      tpin: "",
    });
    setMobileErrors({});
    setDthErrors({});
  }, [activeTab]);

  // Helper function to check for repeating digits
  const isRepeatingDigits = (value) => {
    return /^(\d)\1+$/.test(value);
  };

  // Prevent special characters and spaces in amount and subscriberId fields
  const handleKeyDown = (e, fieldType) => {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Home",
      "End",
      "Enter",
    ];
    const isDigit = /^[0-9]$/.test(e.key);
    const isDecimalPoint = e.key === ".";

    // Allow copy, paste, cut, select-all shortcuts
    if (e.ctrlKey || e.metaKey) {
      const shortcutKeys = ["a", "c", "v", "x"]; // select all, copy, paste, cut
      if (shortcutKeys.includes(e.key.toLowerCase())) {
        return; // do not block
      }
    }

    if (fieldType === "amount") {
      // Allow one decimal point only if not already present
      const currentValue = e.target.value;
      if (isDecimalPoint && currentValue.includes(".")) {
        e.preventDefault();
        return;
      }
      // Block all other keys (e.g., +, -, @, etc.)
      if (!isDigit && !isDecimalPoint && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    } else if (fieldType === "subscriberId") {
      // Allow only digits, block spaces and other characters
      if (!isDigit && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  // Mobile input handlers
  const handleMobileChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "mobileNumber") {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "tpin") {
      processedValue = value.replace(/\D/g, "").slice(0, 4);
      setMobileInputType(processedValue ? "password" : "text");
    } else if (name === "amount") {
      const onlyDigitsRegex = /^\d*$/;

      if (!onlyDigitsRegex.test(value)) {
        setMobileErrors((prev) => ({
          ...prev,
          amount: "Only whole numbers are allowed",
        }));
        return;
      }
    }

    if (name === "operator") {
      const selectedOperator = operatorList.find(
        (op) => op.operatorCode === value
      );
      setMobileForm((prev) => ({
        ...prev,
        operator: value,
        serviceName: selectedOperator ? selectedOperator.serviceName : "",
      }));
    } else {
      setMobileForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    setMobileErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // DTH input handlers
  const handleDthChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "subscriberId") {
      // Allow only digits, no spaces or special characters
      processedValue = value.replace(/[^0-9]/g, "").slice(0, 12);
    } else if (name === "tpin") {
      processedValue = value.replace(/\D/g, "").slice(0, 4);
      setDthInputType(processedValue ? "password" : "text");
    } else if (name === "amount") {
      const onlyDigitsRegex = /^\d*$/;

      if (!onlyDigitsRegex.test(value)) {
        setDthErrors((prev) => ({
          ...prev,
          amount: "Only whole numbers are allowed",
        }));
        return;
      }
    }

    if (name === "operator") {
      const selectedOperator = operatorList.find(
        (op) => op.operatorCode === value
      );
      setDthForm((prev) => ({
        ...prev,
        operator: value,
        serviceName: selectedOperator ? selectedOperator.serviceName : "",
      }));
    } else {
      setDthForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setDthForm((prev) => ({ ...prev, [name]: processedValue }));
    setDthErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Mobile blur handlers
  const handleMobileBlur = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) {
      setMobileErrors((prev) => ({
        ...prev,
        [name]: `${
          name === "mobileNumber"
            ? "Mobile Number"
            : name === "operator"
            ? "Operator"
            : name === "circle"
            ? "Circle"
            : "Amount"
        } is required`,
      }));
      return;
    }
    if (name === "mobileNumber") {
      const mobileRegex = /^[789]\d{9}$/;
      if (!mobileRegex.test(value)) {
        setMobileErrors((prev) => ({
          ...prev,
          mobileNumber:
            "Enter a valid 10-digit mobile number starting with 7, 8, or 9",
        }));
      } else if (isRepeatingDigits(value)) {
        setMobileErrors((prev) => ({
          ...prev,
          mobileNumber: "Mobile Number cannot contain repeating digits",
        }));
      }
    }
    if (name === "amount") {
      const onlyDigitsRegex = /^\d+$/;

      if (!onlyDigitsRegex.test(value)) {
        setMobileErrors((prev) => ({
          ...prev,
          amount: "Only whole numbers are allowed (no decimals or symbols)",
        }));
      } else if (!onlyDigitsRegex.test(value) || parseFloat(value) <= 0) {
        setMobileErrors((prev) => ({
          ...prev,
          amount: "Enter a valid amount",
        }));
      }
    }
  };

  // DTH blur handlers
  const handleDthBlur = (e) => {
    const { name, value } = e.target;
    if (!value.trim()) {
      setDthErrors((prev) => ({
        ...prev,
        [name]: `${
          name === "subscriberId"
            ? "Subscriber ID / Mobile No."
            : name === "operator"
            ? "Operator"
            : "Amount"
        } is required`,
      }));
      return;
    }
    if (name === "subscriberId") {
      const digitRegex = /^\d+$/;
      const noLeadingZero = !value.startsWith("0");
      const noAllZeros = value !== "0".repeat(value.length);
      const lengthValid = value.length >= 10 && value.length <= 12;

      if (!digitRegex.test(value)) {
        setDthErrors((prev) => ({
          ...prev,
          subscriberId: "Subscriber ID must contain only digits",
        }));
      } else if (!lengthValid) {
        setDthErrors((prev) => ({
          ...prev,
          subscriberId: "Subscriber ID must be 10–12 digits",
        }));
      } else if (!noLeadingZero) {
        setDthErrors((prev) => ({
          ...prev,
          subscriberId: "Subscriber ID cannot start with zero",
        }));
      } else if (!noAllZeros) {
        setDthErrors((prev) => ({
          ...prev,
          subscriberId: "Subscriber ID cannot be all zeros",
        }));
      } else if (isRepeatingDigits(value)) {
        setDthErrors((prev) => ({
          ...prev,
          subscriberId: "Subscriber ID cannot contain repeating digits",
        }));
      }
    }

    if (name === "amount") {
      const onlyDigitsRegex = /^\d+$/;

      if (!onlyDigitsRegex.test(value)) {
        setMobileErrors((prev) => ({
          ...prev,
          amount: "Only whole numbers are allowed (no decimals or symbols)",
        }));
      } else if (!onlyDigitsRegex.test(value) || parseFloat(value) <= 0) {
        setMobileErrors((prev) => ({
          ...prev,
          amount: "Enter a valid amount",
        }));
      }
    }
  };

  // Mobile validation
  const validateMobileForm = () => {
    const errors = {};
    const mobileRegex = /^[789]\d{9}$/;
    const amountRegex = /^\d+(\.\d{1,2})?$/; // Matches numbers like 12, 12.3, 12.30

    if (!mobileForm.mobileNumber) {
      errors.mobileNumber = "Mobile Number is required";
    } else if (!mobileRegex.test(mobileForm.mobileNumber)) {
      errors.mobileNumber =
        "Enter a valid 10-digit mobile number starting with 7, 8, or 9";
    } else if (isRepeatingDigits(mobileForm.mobileNumber)) {
      errors.mobileNumber = "Mobile Number cannot contain repeating digits";
    }

    if (!mobileForm.operator) {
      errors.operator = "Operator is required";
    }

    if (!mobileForm.circle) {
      errors.circle = "Circle is required";
    }

    if (!mobileForm.amount) {
      errors.amount = "Amount is required";
    } else if (
      mobileForm.amount.includes("+") ||
      mobileForm.amount.includes("-")
    ) {
      errors.amount = "Positive and negative signs are not allowed";
    } else if (
      !amountRegex.test(mobileForm.amount) ||
      parseFloat(mobileForm.amount) <= 0
    ) {
      errors.amount = "Enter a valid amount";
    } else if (parseFloat(mobileForm.amount) > 5000) {
      errors.amount = "Amount cannot exceed 5000";
    }

    setMobileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // DTH validation
  const validateDthForm = () => {
    const errors = {};
    const amountRegex = /^\d+(\.\d{1,2})?$/; // Matches numbers like 12, 12.3, 12.30
    const digitRegex = /^\d+$/;

    if (!dthForm.subscriberId) {
      errors.subscriberId =
        "Subscriber ID is required / Mobile No. is required";
    } else if (!digitRegex.test(dthForm.subscriberId)) {
      errors.subscriberId = "Subscriber ID must contain only digits";
    } else if (
      dthForm.subscriberId.length < 10 ||
      dthForm.subscriberId.length > 12
    ) {
      errors.subscriberId = "Subscriber ID must be 10–12 digits";
    } else if (dthForm.subscriberId.startsWith("0")) {
      errors.subscriberId = "Subscriber ID cannot start with zero";
    } else if (
      dthForm.subscriberId === "0".repeat(dthForm.subscriberId.length)
    ) {
      errors.subscriberId = "Subscriber ID cannot be all zeros";
    } else if (isRepeatingDigits(dthForm.subscriberId)) {
      errors.subscriberId = "Subscriber ID cannot contain repeating digits";
    }

    if (!dthForm.operator) {
      errors.operator = "Operator is required";
    }

    if (!dthForm.amount) {
      errors.amount = "Amount is required";
    } else if (dthForm.amount.includes("+") || dthForm.amount.includes("-")) {
      errors.amount = "Positive and negative signs are not allowed";
    } else if (
      !amountRegex.test(dthForm.amount) ||
      parseFloat(dthForm.amount) <= 0
    ) {
      errors.amount = "Enter a valid amount";
    } else if (parseFloat(dthForm.amount) > 5000) {
      errors.amount = "Amount cannot exceed 5000";
    }

    setDthErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Mobile Proceed handler with API integration
  const handleMobileProceed = async () => {
    if (validateMobileForm()) {
      try {
        setLoading(true);
        const payload = {
          username: loggedInUserName,
          mobileNo: mobileForm.mobileNumber,
          amount: parseFloat(Number(mobileForm.amount).toFixed(2)),
          operatorCode: mobileForm?.operator,
          serviceType: "MR",
        };

        const headers = {
          rechargekey: rechargeKey,
          "Content-Type": "application/json",
        };

        const response = await axiosPostApi(apiList.RECHARGE, payload, headers);

        if (response.status === 200 && response?.data?.meta?.status === "00") {
          setLoading(false);
          CustomAlert({
            title: "Recharge Successful!",
            text:
              response.data?.meta?.message ||
              "Mobile Recharge Submitted Successfully!",
            icon: "success",
            confirmButtonText: "OK",
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              callApi();
              handleClose();
            }
          });
          // Reset form
          setMobileForm({
            mobileNumber: "",
            operator: "",
            circle: "",
            amount: "",
            tpin: "",
          });
          setMobileInputType("text");
        } else if (
          response?.data?.meta?.status === "03" ||
          response?.data?.meta?.status === "06"
        ) {
          setLoading(false);
          CustomAlert({
            title: "Failed to Request",
            text: response.data.meta?.message,
            icon: "error",
            confirmButtonText: "Retry",
            allowOutsideClick: false,
          });
        } else {
          CustomAlert({
            title: "Failed to Request",
            text: response?.data?.meta?.message,
            icon: "error",
            confirmButtonText: "Retry",
            allowOutsideClick: false,
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: `There was an error: ${error?.message}`,
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // DTH Proceed handler with API integration
  const handleDthProceed = async () => {
    if (validateDthForm()) {
      try {
        const payload = {
          username: loggedInUserName,
          mobileNo: dthForm.subscriberId,
          amount: parseFloat(Number(dthForm.amount).toFixed(2)),
          operatorCode: dthForm?.operator,
          serviceType: "DTH",
        };

        const headers = {
          rechargekey: rechargeKey,
          "Content-Type": "application/json",
        };

        const response = await axiosPostApi(apiList.RECHARGE, payload, headers);

        if (response.status === 200 && response?.data?.meta?.status === "00") {
          CustomAlert({
            title: "DTH Recharge Successful!",
            text:
              response.data?.meta?.message ||
              "DTH Recharge Submitted Successfully!",
            icon: "success",
            confirmButtonText: "OK",
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              callApi();
              handleClose();
            }
          });
          // Reset form
          setDthForm({
            subscriberId: "",
            operator: "",
            amount: "",
            tpin: "",
          });
          setDthInputType("text");
        } else {
          CustomAlert({
            title: "Failed to Request",
            text: response?.data?.meta?.message,
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      } catch (error) {
        console.error("API Error:", error);
        Swal.fire({
          title: "Error",
          text: `There was an error: ${error?.message}`,
          icon: "error",
        });
      }
    }
  };

  return (
    // <div className="rechargeContainer">
    <div>
      <div className="recharge-tab-container mt-[30px]">
        {tabs.map((tab) => {
          const icon =
            tab.id === "mobile"
              ? activeTab === "mobile"
                ? MobileVectorWhiteIcon
                : MobileVectorIcon
              : activeTab === "dth"
              ? DTHVectorWhiteIcon
              : DTHVectorIcon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`recharge-tab-button ${
                activeTab === tab.id
                  ? "recharge-tab-button-active"
                  : "!bg-[#d3d3d3]"
              }`}
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src={icon}
                alt={`${tab.label} icon`}
                style={{ marginRight: "10px" }}
              />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* <div className="recharge-tab-content"> */}
      <div className="p-6">
        {activeTab === "mobile" && (
          <div style={{ display: "flex", gap: "15%" }}>
            <div>
              <h2 className="heading">Mobile Recharge Details</h2>
              <div className="input-container">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    className="input-field"
                    maxLength={10}
                    value={mobileForm.mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        // only numbers allowed
                        handleMobileChange(e);
                      }
                    }}
                    onBlur={handleMobileBlur}
                  />
                  {mobileErrors.mobileNumber && (
                    <span className="error">{mobileErrors.mobileNumber}</span>
                  )}
                </div>
                <div className="input-wrapper">
                  <select
                    name="operator"
                    className="select-field"
                    value={mobileForm.operator}
                    onChange={handleMobileChange}
                    onBlur={handleMobileBlur}
                  >
                    <option value="">Select Operator</option>
                    <div className="h-5">
                      {operatorList?.map((op) => (
                        <option key={op.operatorCode} value={op.operatorCode}>
                          {op.operatorName}
                        </option>
                      ))}
                    </div>
                  </select>
                  {mobileErrors.operator && (
                    <span className="error">{mobileErrors.operator}</span>
                  )}
                </div>

                <div className="input-wrapper">
                  <select
                    name="circle"
                    className="select-field"
                    value={mobileForm.circle}
                    onChange={handleMobileChange}
                    onBlur={handleMobileBlur}
                  >
                    <option value="">Select Circle</option>
                    <option>Maharashtra</option>
                    <option>Delhi</option>
                    <option>Karnataka</option>
                  </select>
                  {mobileErrors.circle && (
                    <span className="error">{mobileErrors.circle}</span>
                  )}
                </div>
                <div className="input-wrapper">
                  <input
                    type="text"
                    // step="0.01"
                    name="amount"
                    placeholder="Amount"
                    className="input-field"
                    value={mobileForm.amount}
                    // onChange={handleMobileChange}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        // only numbers allowed
                        handleMobileChange(e);
                      }
                    }}
                    onBlur={handleMobileBlur}
                    // onKeyDown={handleAmountKeyDown}
                  />
                  {mobileErrors.amount && (
                    <span className="error">{mobileErrors.amount}</span>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-4 px-4 mt-[30px] border-t pt-4">
                <button
                  type="button"
                  className="btn btn-back"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-submit"
                  onClick={handleMobileProceed}
                  disabled={loading}
                >
                  Submit
                </button>
              </div>
            </div>
            {/* <div>
              <img
                src={MobileImage}
                alt="mobile"
                style={{ height: "95%", width: "85%" }}
              />
            </div> */}
          </div>
        )}

        {activeTab === "dth" && (
          <div style={{ display: "flex", gap: "15%" }}>
            <div>
              <h2 className="heading">DTH Recharge Details</h2>
              <div className="input-container">
                <div className="input-wrapper full-width">
                  <input
                    type="text"
                    name="subscriberId"
                    placeholder="Subscriber ID / Mobile No"
                    className="input-field full-width"
                    maxLength={12}
                    value={dthForm.subscriberId}
                    onChange={handleDthChange}
                    onBlur={handleDthBlur}
                    onKeyDown={(e) => handleKeyDown(e, "subscriberId")}
                  />
                  {dthErrors.subscriberId && (
                    <span className="error">{dthErrors.subscriberId}</span>
                  )}
                </div>
                {/* <div className="input-wrapper">
                  <select
                    name="operator"
                    className="select-field"
                    value={dthForm.operator}
                    onChange={handleDthChange}
                    onBlur={handleDthBlur}
                  >
                    <option value="">Select Operator</option>
                    <option>Tata Sky</option>
                    <option>Airtel DTH</option>
                    <option>Dish TV</option>
                  </select>
                  {dthErrors.operator && (
                    <span className="error">{dthErrors.operator}</span>
                  )}
                </div> */}

                <div className="input-wrapper">
                  <select
                    name="operator"
                    className="select-field"
                    value={dthForm.operator}
                    onChange={handleDthChange}
                    onBlur={handleDthBlur}
                  >
                    <option value="">Select Operator</option>
                    {operatorList?.map((op) => (
                      <option key={op.operatorCode} value={op.operatorCode}>
                        {op.operatorName}
                      </option>
                    ))}
                  </select>
                  {dthErrors.operator && (
                    <span className="error">{dthErrors.operator}</span>
                  )}
                </div>
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="amount"
                    placeholder="Amount"
                    className="input-field"
                    value={dthForm.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        // only numbers allowed
                        handleDthChange(e);
                      }
                    }}
                    onBlur={handleDthBlur}
                    // onKeyDown={handleAmountKeyDown}
                  />
                  {dthErrors.amount && (
                    <span className="error">{dthErrors.amount}</span>
                  )}
                </div>
                {/* <button className="proceed-button" onClick={handleDthProceed}>
                  Proceed
                </button> */}
              </div>

              <div className="flex justify-end gap-4 px-4 mt-[30px] border-t pt-4">
                <button
                  type="button"
                  className="btn btn-back"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-submit"
                  onClick={handleDthProceed}
                  disabled={loading}
                >
                  Submit
                </button>
              </div>
            </div>
            {/* <div>
              <img
                src={DTHImage}
                alt="dth"
                style={{ height: "95%", width: "85%" }}
              />
            </div> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default Recharge;
